import dayjs from 'dayjs';
import { useMemo } from 'react';

/**
 * 날짜 포맷팅
 * - 시간 단위 표시하지 않고, 'N일 전'을 표시할 때 '어제', '오늘'이라고 표기함
 */
export const CommonDateFormat = ({
    date,
    fromNow = false,
}: {
    date: string | Date | dayjs.Dayjs | undefined;
    fromNow?: boolean;
}) => {
    const today = useMemo(() => dayjs(), []);

    const formattedDate = useMemo(() => {
        if (!date) {
            return '';
        }

        // 서버 시간은 기본 한국시간
        const d = dayjs(date).tz('Asia/Seoul', true);

        // 3일 이전이면 fromNow 여도 포맷대로 보여줌
        if (fromNow) {
            const diffDay = d.diff(today, 'day');
            if (diffDay === 0) {
                return '오늘';
            } else if (diffDay === -1) {
                return '어제';
            } else if (diffDay > -3) {
                return d.fromNow();
            }
        }

        return d.format('YYYY-MM-DD');
    }, [date, fromNow, today]);

    return <>{formattedDate}</>;
};

/**
 * 영상 길이 포맷팅 (초 → "X초" / "X분" / "X시간 Y분")
 * - round: 'second' → 초 단위, 'minute' → 분 단위로 반올림 (기본: 'minute')
 */
export const CommonDurationFormat = ({
    seconds,
    round = 'minute',
}: {
    seconds: number;
    round?: 'second' | 'minute';
}) => {
    let formatted: string;
    if (round === 'second') {
        const h = Math.floor(seconds / 3600);
        const m = Math.floor((seconds % 3600) / 60);
        const s = seconds % 60;
        if (h > 0) {
            formatted = `${h}시간 ${m}분 ${s}초`;
        } else if (m > 0) {
            formatted = `${m}분 ${s}초`;
        } else {
            formatted = `${s}초`;
        }
    } else {
        const totalMinutes = Math.round(seconds / 60);
        const h = Math.floor(totalMinutes / 60);
        const m = totalMinutes % 60;
        formatted = h > 0 ? `${h}시간 ${m}분` : `${m}분`;
    }
    return <>{formatted}</>;
};

/**
 * 시간 포맷팅
 */
export const CommonTimeFormat = ({
    time,
    fromNow = false,
    isShowHour = true,
    isShowSecond = false,
}: {
    time: string | Date | dayjs.Dayjs | undefined;
    fromNow?: boolean;
    isShowHour?: boolean;
    isShowSecond?: boolean;
}) => {
    const today = useMemo(() => dayjs(), []);

    const formattedDate = useMemo(() => {
        if (!time) {
            return '';
        }

        // 서버 시간은 기본 한국시간
        const d = dayjs(time).tz('Asia/Seoul', true);

        // 3일 이전이면 fromNow 여도 포맷대로 보여줌
        if (fromNow && d.diff(today, 'day') > -3) {
            return d.fromNow();
        }

        let format = 'YYYY-MM-DD';
        if (isShowHour) {
            format += ' HH:mm';
            if (isShowSecond) {
                format += ':ss';
            }
        }

        return d.format(format);
    }, [time, fromNow, isShowHour, today]);

    return <>{formattedDate}</>;
};
